import * as yaml from 'yaml';

interface Rule {
  condition?: string;
  rules?: Rule[];
  anchor_name?: string;
}

interface Anchor {
  condition: string;
  description: string;
}

interface JsonRules {
  assetType?: string;
  anchors: Record<string, Anchor>;
  rules: any[];
}

// Operator mappings
const operatorMappingInverted: Record<string, string> = {
  eq: '==',
  lte: '<=',
  lt: '<',
  gt: '>',
  gte: '>=',
  neq: '!=',
  is: 'is',
};

const arithOperatorMapping: Record<string, string> = {
  "+": 'add',
  "-": 'sub',
  "*": 'mul',
  "/": 'div',
};

const operatorMapping: Record<string, string> = {
  "==": 'eq',
  "=": 'eq',
  "!=": 'neq',
  "<": 'lt',
  "<=": 'lte',
  ">": 'gt',
  ">=": 'gte',
  "is": 'eq'
};

const buildAnchors = (newYaml: yaml.Document, anchors: Record<string, any>) => {
  for (const [key, value] of Object.entries(anchors || {})) {
    const regex = /^(\S+)\.(\S+)\s*([=!<>is]+)\s*(.*)$/;
    const match = value.condition?.match(regex);

    if (match) {
      const [, stateName, condName, operator, rawVal] = match;

      let val: any = rawVal;
      let seq = 0;

      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1); // Remove quotes
      }
      if (val === 'None') {
        val = 'none';
      }

      // Handle numeric values with decimals (like 2.5 -> 2_pt_5)
      const regexDec = /^(\d+)\.(\d+)$/;
      const matchDec = val.match(regexDec);
      if (matchDec) {
        val = `${matchDec[1]}_pt_${matchDec[2]}`;
      }

      let anchorName = '';
      if (stateName === '_sys_attr') {
        anchorName = `system_${condName}_${operator}_${val}`;
      } else if (stateName === '_state') {
        if (condName === 'in_business_hour' && val === '2') {
          val = 'none';
        }
        anchorName = `status_${condName}_${operator}_${val}`;
      } else {
        if (seq !== 0) {
          anchorName = `${stateName}_${seq}_${condName}_${operator}_${val}`;
        } else {
          anchorName = `${stateName}_${condName}_${operator}_${val}`;
        }
      }

      // Find operator from operatorMappingInverted
      const op = Object.keys(operatorMappingInverted).find(
        (k) => operatorMappingInverted[k] === operator
      );

      let cond: string | undefined;
      if (value.condition.type === 'double' || value.condition.type === 'integer') {
        cond = `${stateName}.${condName} ${op} ${val}`;
      } else if (value.condition.type === 'string') {
        if (val === 'none') {
          cond = `${stateName}.${condName} is None`;
        } else if (isNaN(Number(val))) {
          cond = `${stateName}.${condName} ${op} "${val}"`;
        } else {
          cond = `${stateName}.${condName} ${op} ${Number(val)}`;
        }
      }

      const anch = newYaml.createNode({
        condition: value.condition,
        description: value.description,
      });
      anch.anchor = key;

      newYaml.set(key, anch);
    } else {
      console.log("Anchor NO MATCH: ", value)
    }
  }
};

const addCondNode = (cond: string): string | null => {
  // Regex to capture the pattern "<field> <operator> <value>"
  const regex = /^(\S+)\.(\S+)\s*([=!<>is]+)\s*(.+)$/;
  const match = cond.match(regex);
  let stateName: string | undefined;
  let condName: string | undefined;
  let anchorName: string | undefined;
  let seq = 0;

  if (match) {
    const operator = match[3]; // =, ==, !=, <=, <, >=, >
    let val = match[4]; // 0 (or any other value)
    if (val.startsWith('"') && val.endsWith('"')) {
      val = val.slice(1, -1); // Remove quotes
    }
    if (val === 'None') {
      val = 'none'
    }

    stateName = match[1];
    condName = match[2];
    let op = operatorMapping[operator];
    
    // check for field-to-field value mappings
    const valueRegex = /^(?=.*[a-zA-Z_])[a-zA-Z0-9_]+\.[a-zA-Z0-9_.]+(\s*([\+\-\*\/]?\s*\d+(\.\d+)?))?$/;
    let isFieldToField = valueRegex.test(val) && !/^\d+\.\d+$/.test(val.split(/\s+/)[0]);
    if (typeof val === 'string' && val.startsWith('_parameter.')) {
      // HANDLE parameter type value
      console.log("NEW _parameter VALUE: ", stateName, val)
      let dupValue = val;

      dupValue = dupValue.replace("_parameter.", "");
      const valueRegex = /^(?=.*[a-zA-Z_])[a-zA-Z0-9_]+\.[a-zA-Z0-9_.]+(\s*([\+\-\*\/]?\s*\d+(\.\d+)?))?$/;
      let isFieldToField = valueRegex.test(dupValue) && !/^\d+\.\d+$/.test(dupValue.split(/\s+/)[0]);

      // HANDLE parameter field-to-field mapping
      if (typeof dupValue === 'string' && isFieldToField) {
        const fieldRegex = /_(\d+)\./;
        const fieldMatch = (stateName + '.').match(fieldRegex);
        let sequence = 0;
        if (fieldMatch) {
          sequence = Number(fieldMatch[1]);
        }
  
        if (sequence > 0) {
          const dotIndex = dupValue.indexOf('.');
          let component = dupValue.substring(0, dotIndex)
          component = component.replace(/(_\d+)$/, '');
          component = component + '_' + sequence;
  
          dupValue = component + '_' + dupValue.substring(dotIndex + 1)
        }
  
        const parts = dupValue.replace(/^\$/, '').trim().split(' ');
        dupValue = `${parts[0].replaceAll('.', '_')}`;
        if (parts.length ===3) {
          let op = arithOperatorMapping[parts[1]];
          // check for decimal values in value
          let fieldValue = parts[2]
          const regexDec = /^(\d+)\.(\d+)$/;
          const matchDec = fieldValue.toString().match(regexDec);
          if (matchDec) {
            fieldValue = `${matchDec[1]}_pt_${matchDec[2]}`;
          }
  
          dupValue = `${parts[0].replaceAll('.', '_')}_${op}_${fieldValue}`
        }
      } else {
        // HANDLE parameter field-to-value mapping
        const regexDec = /^(\d+)\.(\d+)$/;
        const matchDec = dupValue.toString().match(regexDec);
        if (matchDec) {
          dupValue = `${matchDec[1]}_pt_${matchDec[2]}`;
        }
      }

      val = `_parameter_${dupValue}`;
    } else if (typeof val === 'string' && (val.startsWith('$') || isFieldToField)) {
      // HANDLE field-to-field mapping value
      const fieldRegex = /_(\d+)\./;
      const fieldMatch = (stateName + '.').match(fieldRegex);
      let sequence = 0;
      if (fieldMatch) {
        sequence = Number(fieldMatch[1]);
      }

      console.log("NEW field-to-field sequence: ", stateName, fieldMatch,  sequence)
      if (sequence > 0) {
        const dotIndex = val.indexOf('.');
        let component = val.substring(0, dotIndex)
        component = component.replace(/(_\d+)$/, '');
        component = component + '_' + sequence;

        val = component + '_' + val.substring(dotIndex + 1)
      }

      const parts = val.replace(/^\$/, '').trim().split(' ');
      val = `${parts[0].replaceAll('.', '_')}`;
      if (parts.length ===3) {
        let op = arithOperatorMapping[parts[1]];
        // check for decimal values in value
        let fieldValue = parts[2]
        const regexDec = /^(\d+)\.(\d+)$/;
        const matchDec = fieldValue.toString().match(regexDec);
        if (matchDec) {
          fieldValue = `${matchDec[1]}_pt_${matchDec[2]}`;
        }

        val = `${parts[0].replaceAll('.', '_')}_${op}_${fieldValue}`
      }
    } else {
      // HANDLE field-to-value mapping
      const regexDec = /^(\d+)\.(\d+)$/;
      const matchDec = val.toString().match(regexDec);
      if (matchDec) {
        val = `${matchDec[1]}_pt_${matchDec[2]}`;
      }
    }

    if (stateName === '_sys_attr') {
      anchorName = `system_${condName}_${op}_${val}`;
    } else if (stateName === '_state') {
      if (condName === 'in_business_hour') {
        if (val === "2") {
          val = 'none';
        }
      }
      anchorName = `status_${condName}_${op}_${val}`;
    } else {
      if (seq !== 0 && seq !== undefined) {
        anchorName = `${stateName}_${seq}_${condName}_${op}_${val}`;
      } else {
        anchorName = `${stateName}_${condName}_${op}_${val}`;
      }
    }

    return anchorName;
  } else {
    console.log("NO MATCH: ", cond)
  }

  return null;
};

const ruleFormatter = (cond: string): string => {
  // Regex to capture the pattern "<field> <operator> <value>"
  const regex = /^(\S+)\.(\S+)\s*([=!<>is]+)\s*(.+)$/;
  const match = cond.match(regex);
  let stateName: string | undefined;
  let condName: string | undefined;

  if (match) {
    let val = match[4]; // 0 (or any other value)
    if (val.startsWith('"') && val.endsWith('"')) {
      val = val.slice(1, -1); // Remove quotes
    }
    if (val === 'None') {
      val = 'none'
    }

    stateName = match[1];
    condName = match[2];
    let op = match[3];  // =, ==, !=, <=, <, >=, >

    // replace = with == for yaml consistency
    if (op === '=') {
      op = '==';
    }
    
    // check for field-to-field value mappings
    const valueRegex = /^(?=.*[a-zA-Z_])[a-zA-Z0-9_]+\.[a-zA-Z0-9_.]+(\s*([\+\-\*\/]?\s*\d+(\.\d+)?))?$/;
    let isFieldToField = valueRegex.test(val) && !/^\d+\.\d+$/.test(val.split(/\s+/)[0]);
    if (typeof val === 'string' && val.startsWith('_parameter.')) {
      // HANDLE parameter type value
      console.log("NEW _parameter RULE: ", stateName, val)
      let dupValue = val;

      dupValue = dupValue.replace("_parameter.", "");
      const valueRegex = /^(?=.*[a-zA-Z_])[a-zA-Z0-9_]+\.[a-zA-Z0-9_.]+(\s*([\+\-\*\/]?\s*\d+(\.\d+)?))?$/;
      let isFieldToField = valueRegex.test(dupValue) && !/^\d+\.\d+$/.test(dupValue.split(/\s+/)[0]);

      let rule;
      // HANDLE parameter field-to-field mapping
      if (typeof dupValue === 'string' && isFieldToField) {
        const fieldRegex = /_(\d+)\./;
        const fieldMatch = (stateName + '.').match(fieldRegex);
        let sequence = 0;
        if (fieldMatch) {
          sequence = Number(fieldMatch[1]);
        }

        if (sequence > 0) {
          const dotIndex = dupValue.indexOf('.');
          let component = dupValue.substring(0, dotIndex)
          component = component.replace(/(_\d+)$/, '');
          component = component + '_' + sequence;
          
          dupValue = component + '.' + dupValue.substring(dotIndex + 1)
        }
        dupValue = `_parameter.${dupValue}`
        
        rule = `${stateName}.${condName} ${op} ${dupValue}`
        console.log("NEW field-to-field Paramter RULE: ", rule)
      } else {
        rule = `${stateName}.${condName} ${op} ${val}`
      }
      
      return rule
    } else if (typeof val === 'string' && (val.startsWith('$') || isFieldToField)) {
      const fieldRegex = /_(\d+)\./;
      const fieldMatch = (stateName + '.').match(fieldRegex);
      let sequence = 0;
      if (fieldMatch) {
        sequence = Number(fieldMatch[1]);
      }

      if (sequence > 0) {
        const dotIndex = val.indexOf('.');
        let component = val.substring(0, dotIndex)
        component = component.replace(/(_\d+)$/, '');
        component = component + '_' + sequence;
        
        val = component + '.' + val.substring(dotIndex + 1)
      }
      
      let rule = `${stateName}.${condName} ${op} ${val}`
      console.log("NEW field-to-field RULE: ", rule)
      return rule
    } else {
      let rule = `${stateName}.${condName} ${op} ${val}`
      return rule
    }
  } else {
    console.log("NO MATCH: ", cond)
  }

  return cond;
};

const buildYamlNode = (
  newYaml: yaml.Document,
  rule: Rule,
  topLevel = true
): yaml.Node | null => {
  if (rule.condition) {
    if (rule.condition === 'AND') {
      const andNode = newYaml.createNode({ AND: [] });
      rule.rules?.forEach((subRule) => {
        (andNode as any).items[0].value.items.push(buildYamlNode(newYaml, subRule, false));
      });
      
      if (topLevel) {
        return andNode;
      } else {
        var condNode = newYaml.createNode({ condition: { }});
        condNode.set('condition', andNode);
        return condNode;
      }
    } else if (rule.condition === 'OR') {
      const orNode = newYaml.createNode({ OR: [] });
      rule.rules?.forEach((subRule) => {
        (orNode as any).items[0].value.items.push(buildYamlNode(newYaml, subRule, false));
      });
      
      if (topLevel) {
        return orNode;
      } else {
        var condNode = newYaml.createNode({ condition: { }});
        condNode.set('condition', orNode);
        return condNode;
      }
    }
  } else {
    const anchor_name = addCondNode(rule)?.trim()
    if (anchor_name) {
      if (!newYaml.get(anchor_name)) {
        let formattedRule = ruleFormatter(rule.replace('$', '').trim())
        const anch = newYaml.createNode({
          condition: formattedRule,
          description: formattedRule.replace('.', ' - '),
        });
        anch.anchor = anchor_name;
        newYaml.set(anchor_name, anch);
      }
      const alias = new yaml.Alias(anchor_name);
      return newYaml.createNode(alias);
    } else {
      return newYaml.createNode({ condition: addCondNode(rule) });
    }
  }
  return null;
};

const jsonToYaml = (jsonRules: JsonRules): yaml.Document => {
  try {
    const newYaml = new yaml.Document(new yaml.YAMLMap());
    buildAnchors(newYaml, jsonRules.anchors);

    const yamlRulesList = jsonRules.rules.map((jsonRule) => {
      const yamlRule: Record<string, any> = {
        code: jsonRule.code,
        description: jsonRule.description,
        status: jsonRule.status,
        deploy: jsonRule.deploy || "enable",
        back_to_normal_alert: jsonRule.back_to_normal_alert,
        summary: jsonRule.summary,
        recommended_action: jsonRule.recommended_action,
      };

      if (jsonRule.prerequisite) {
        yamlRule['prerequisite'] = buildYamlNode(newYaml, jsonRule.prerequisite, true);
      }

      if (jsonRule.trigger_pre) {
        yamlRule['trigger_pre'] = buildYamlNode(newYaml, jsonRule.trigger_pre, true);
      }

      if (jsonRule.trigger_post) {
        yamlRule['trigger_post'] = buildYamlNode(newYaml, jsonRule.trigger_post, true);
      }

      if (jsonRule.email_condition) {
        yamlRule['email_condition'] = buildYamlNode(newYaml, jsonRule.email_condition, true);
      }

      return yamlRule;
    });

    // set asset_type after anchors
    if (jsonRules.assetType) {
      newYaml.add(newYaml.createPair('asset_type', jsonRules.assetType));
    }

    // set _rules after asset_type
    newYaml.set('_rules', newYaml.createNode({ Default: yamlRulesList }));

    return newYaml;
  } catch (e) {
    console.error('jsonToYaml ERROR -', e);
    throw e;
  }
};

export default jsonToYaml;
